class AutocompleteFilters {
  constructor () {
    this.eventBus = window.theme.autocompleteResults.eventBus
    this.stateLayer = window.theme.autocompleteResults.stateLayer

    // Subscribe to filter events
    this.subscribeToFilterEvents()
  }

  /**
   * Initialize filters from URL on page load - called by API layer
   */
  initializeFiltersFromUrl () {
    const url = new URL(window.location.href)
    const filtersParam = url.searchParams.get('search_filters')

    const activeFacets = []

    if (filtersParam) {
      const filterGroups = filtersParam.split('&f-')
      filterGroups[0] = filterGroups[0].replace(/^f-/, '')

      if (
        filterGroups.length > 0 &&
        filterGroups[filterGroups.length - 1].endsWith('&')
      ) {
        filterGroups[filterGroups.length - 1] = filterGroups[
          filterGroups.length - 1
        ].replace(/&$/, '')
      }

      filterGroups.forEach(filterPair => {
        const [facetKey, facetValues] = filterPair.split('=')

        const key = facetKey

        const values = facetValues
          .split(',')
          .map(value => decodeURIComponent(value))

        const existingFacetIndex = activeFacets.findIndex(
          facet => facet.key === facetKey,
        )

        if (existingFacetIndex !== -1) {
          activeFacets[existingFacetIndex].values.push(...values)
        } else {
          activeFacets.push({ key, values, singleSelect: false })
        }
      })
    }

    return activeFacets
  }

  /**
   * Check if filters should be reset for a new search
   */
  shouldResetFilters (searchTerm) {
    const previousSearchTerm = sessionStorage.getItem('currentSearchTerm')
    return previousSearchTerm !== null && previousSearchTerm !== searchTerm
  }

  /**
   * Clear filters from URL
   */
  clearFiltersFromUrl () {
    const url = new URL(window.location.href)
    url.searchParams.delete('search_filters')
    window.history.replaceState({ path: url.toString() }, '', url.toString())
  }

  /**
   * Updates the filters URL parameter based on the current activeFacets
   */
  updateFiltersUrlParameter () {
    const activeFacets = this.stateLayer.activeFacets
    const filtersString = activeFacets
      .map(
        facet =>
          `f-${facet.key}=${facet.values
            .map(value => encodeURIComponent(value))
            .join(',')}`,
      )
      .join('&')

    const url = new URL(window.location.href)
    if (filtersString) {
      url.searchParams.set('search_filters', filtersString)
    } else {
      url.searchParams.delete('search_filters')
    }
    window.history.pushState({ path: url.toString() }, '', url.toString())
  }

  // Allows us to show/hide the greyed out filter options if necessary.
  updateFilterAppliedAttributeFromUrl () {
    // Only apply to desktop filters.
    const facetsEls = document.querySelectorAll(
      '.hd-Search .clc-ProductGrid_TabletFacetsInner [data-collection-filters-form-el="facets"]',
    )
    if (!facetsEls.length) return

    const url = new URL(window.location.href)
    const hasFilters = url.searchParams.get('search_filters')

    facetsEls.forEach(facetsEl => {
      if (hasFilters) {
        facetsEl.setAttribute('data-filter-applied', '')
      } else {
        facetsEl.removeAttribute('data-filter-applied')
      }
    })
  }

  /**
   * Subscribe to filter-related events
   */
  subscribeToFilterEvents () {
    this.eventBus.subscribe('facets-list:apply-filter', ({ facet, option }) => {
      const lowerCaseFacetKey = facet.toLowerCase()
      const existingFacetIndex = this.stateLayer.activeFacets.findIndex(
        activeFacet => activeFacet.key === lowerCaseFacetKey,
      )

      let updatedFacets

      if (existingFacetIndex !== -1) {
        updatedFacets = this.stateLayer.activeFacets.map(
          (activeFacet, index) => {
            if (index === existingFacetIndex) {
              return {
                ...activeFacet,
                values: [...activeFacet.values, option],
              }
            }
            return activeFacet
          },
        )
      } else {
        updatedFacets = [
          ...this.stateLayer.activeFacets,
          {
            key: lowerCaseFacetKey,
            values: [option],
            singleSelect: false,
          },
        ]
      }

      this.stateLayer.activeFacets = updatedFacets
      this.updateFiltersUrlParameter()
    })

    this.eventBus.subscribe(
      'facets-list:remove-filter',
      ({ facet, option }) => {
        const lowerCaseFacetKey = facet.toLowerCase()
        let updatedFacets = this.stateLayer.activeFacets

        if (lowerCaseFacetKey === 'klevu_price') {
          updatedFacets = this.stateLayer.activeFacets.filter(
            activeFacet => activeFacet.key !== lowerCaseFacetKey,
          )

          this.stateLayer.activeFacets = updatedFacets
          this.updateFiltersUrlParameter()

          this.eventBus.publish('state:activeFacets:removed')
          this.eventBus.publish('search:state:resetPriceRange')

          return
        }

        const facetIndex = this.stateLayer.activeFacets.findIndex(
          activeFacet => activeFacet.key === lowerCaseFacetKey,
        )

        if (facetIndex !== -1) {
          const targetFacet = this.stateLayer.activeFacets[facetIndex]

          const newValues = targetFacet.values.filter(val => val !== option)

          if (newValues.length === 0) {
            updatedFacets = this.stateLayer.activeFacets.filter(
              (_, index) => index !== facetIndex,
            )
          } else if (newValues.length < targetFacet.values.length) {
            updatedFacets = this.stateLayer.activeFacets.map(
              (activeFacet, index) => {
                if (index === facetIndex) {
                  return { ...activeFacet, values: newValues }
                }
                return activeFacet
              },
            )
          }
        }

        this.stateLayer.activeFacets = updatedFacets
        this.updateFiltersUrlParameter()

        this.eventBus.publish('state:activeFacets:removed')
      },
    )

    this.eventBus.subscribe('facets-list:remove-all-filters', () => {
      this.stateLayer.activeFacets = []
      this.updateFiltersUrlParameter()

      this.eventBus.publish('state:resetPriceRange')
      this.eventBus.publish('state:activeFacets:removed')
    })

    this.eventBus.subscribe('price-range:change', ({ min, max }) => {
      const existingFacetIndex = this.stateLayer.activeFacets.findIndex(
        activeFacet => activeFacet.key === 'klevu_price',
      )

      let updatedFacets

      if (existingFacetIndex !== -1) {
        // If the facet already exists, update its values
        updatedFacets = this.stateLayer.activeFacets.map(
          (activeFacet, index) => {
            if (index === existingFacetIndex) {
              return {
                ...activeFacet,
                values: [min, max],
              }
            }
            return activeFacet
          },
        )
      } else {
        // If the facet doesn't exist, add a new one
        updatedFacets = [
          ...this.stateLayer.activeFacets,
          { key: 'klevu_price', values: [min, max], singleSelect: false },
        ]
      }

      this.stateLayer.activeFacets = updatedFacets
      this.updateFiltersUrlParameter()
    })
  }
}

window.theme.autocompleteResults.filters = new AutocompleteFilters()
